import { WORDS } from '../constants/wordlist'
import { VALIDGUESSES } from '../constants/validGuesses'

const VOWELS = new Set(['A', 'E', 'I', 'O', 'U'])
const SOMETIMES_Y = 'Y'

const TREAT_Y_AS_VOWEL = false

type Stats = {
  N: number
  letterFreq: Record<string, number>
  posFreq: Array<Record<string, number>> // per-position letter counts
  bigramFreq: Record<string, number>
  commonDoubleSet: Set<string>
  patternBuckets: Map<string, number> // for confusability
}

function buildStats(valid: string[], wordLen = 5): Stats {
  const letterFreq: Record<string, number> = {}
  const posFreq: Array<Record<string, number>> = Array.from({ length: wordLen }, () => ({}))
  const bigramFreq: Record<string, number> = {}
  const patternBuckets: Map<string, number> = new Map()

  const up = valid
    .map(w => w.trim().toUpperCase())
    .filter(w => w.length === wordLen && /^[A-Z]+$/.test(w))

  for (const w of up) {
    // letter freq & positional freq
    for (let i = 0; i < wordLen; i++) {
      const ch = w[i]
      letterFreq[ch] = (letterFreq[ch] ?? 0) + 1
      const pf = posFreq[i]
      pf[ch] = (pf[ch] ?? 0) + 1
    }
    // bigrams
    for (let i = 0; i < wordLen - 1; i++) {
      const b = w.slice(i, i + 2)
      bigramFreq[b] = (bigramFreq[b] ?? 0) + 1
    }
    // confusability pattern (mask letters by identity index)
    // e.g., "LEVEL" -> pattern "0,1,2,1,3"
    const pat = toPattern(w)
    patternBuckets.set(pat, (patternBuckets.get(pat) ?? 0) + 1)
  }

  // Very common doubles to *lightly* exempt from repeat penalty
  const commonDoubleSet = new Set<string>()
  for (const [bg, cnt] of Object.entries(bigramFreq)) {
    if (bg[0] === bg[1] && cnt >= 50) { // threshold tweakable
      commonDoubleSet.add(bg)
    }
  }

  return {
    N: up.length,
    letterFreq,
    posFreq,
    bigramFreq,
    commonDoubleSet,
    patternBuckets,
  }
}

function toPattern(w: string): string {
  const map = new Map<string, number>()
  let next = 0
  const ids = []
  for (const ch of w) {
    if (!map.has(ch)) map.set(ch, next++)
    ids.push(map.get(ch)!)
  }
  return ids.join(',')
}

function norm01(x: number, min: number, max: number): number {
  if (max <= min) return 0
  const n = (x - min) / (max - min)
  return n < 0 ? 0 : n > 1 ? 1 : n
}

function isVowel(ch: string): boolean {
  if (VOWELS.has(ch)) return true
  if (TREAT_Y_AS_VOWEL && ch === SOMETIMES_Y) return true
  return false
}

function consonantRunStats(w: string): { maxRun: number; numRuns3Plus: number } {
  let maxRun = 0
  let cur = 0
  let runs3p = 0
  for (const ch of w) {
    if (!isVowel(ch)) {
      cur++
      maxRun = Math.max(maxRun, cur)
    } else {
      if (cur >= 3) runs3p++
      cur = 0
    }
  }
  if (cur >= 3) runs3p++
  return { maxRun, numRuns3Plus: runs3p }
}

export function scoreDifficulty(word: string, stats: Stats, wordLen = 5): number {
  const w = word.toUpperCase()
  if (w.length !== wordLen) return 50

  // 1) Letter rarity (sum inverse frequency)
  // normalize by dividing by max achievable given corpus
  let letterRarity = 0
  for (const ch of w) {
    const f = stats.letterFreq[ch] ?? 1
    letterRarity += 1 / f
  }
  // Compute rough normalization scale
  const maxInv = 1 / 1 // if letter never appears, we used 1 fallback, keep it bounded
  const letterRarityNorm = norm01(letterRarity, 0, wordLen * maxInv)

  // 2) Positional rarity
  let posRarity = 0
  for (let i = 0; i < wordLen; i++) {
    const ch = w[i]
    const f = stats.posFreq[i][ch] ?? 1
    posRarity += 1 / f
  }
  const posRarityNorm = norm01(posRarity, 0, wordLen * maxInv)

  // 3) Vowel structure
  const vowels = Array.from(w).filter(isVowel).length
  const hasAEIOU = Array.from(w).some(ch => VOWELS.has(ch))
  let vowelPenalty = 0
  if (vowels <= 1) vowelPenalty = 1.0 // very hard
  else if (vowels === 2) vowelPenalty = 0.4
  else if (vowels === 3) vowelPenalty = 0.2
  else vowelPenalty = 0.5 // 4–5 vowels: easier to place but also trick guesses
  if (!hasAEIOU && w.includes('Y')) vowelPenalty = Math.max(vowelPenalty, 0.8) // “only Y” is tough

  // 4) Repeats
  const counts: Record<string, number> = {}
  for (const ch of w) counts[ch] = (counts[ch] ?? 0) + 1
  let repeatPenalty = 0
  for (const [ch, c] of Object.entries(counts)) {
    if (c > 1) {
      // common doubles get a lighter penalty
      const bg = ch + ch
      repeatPenalty += stats.commonDoubleSet.has(bg) ? 0.15 : 0.35 * (c - 1)
    }
  }
  repeatPenalty = Math.min(repeatPenalty, 1)

  // 5) Consonant clusters
  const { maxRun, numRuns3Plus } = consonantRunStats(w)
  let clusterPenalty = 0
  if (maxRun >= 3) clusterPenalty += 0.4
  if (maxRun >= 4) clusterPenalty += 0.3
  if (maxRun >= 5) clusterPenalty += 0.3
  clusterPenalty += Math.min(0.3, numRuns3Plus * 0.15)
  clusterPenalty = Math.min(clusterPenalty, 1)

  // 6) Bigram rarity
  let bigramRarity = 0
  for (let i = 0; i < wordLen - 1; i++) {
    const bg = w.slice(i, i + 2)
    const f = stats.bigramFreq[bg] ?? 0
    if (f === 0) bigramRarity += 1.0
    else bigramRarity += 1 / Math.min(50, f) // cap to dampen extremely common pairs
  }
  const bigramRarityNorm = norm01(bigramRarity, 0, wordLen - 1) // unseen everywhere ≈ max

  // 7) Confusability (more neighbors => easier, so subtract from difficulty)
  const pat = toPattern(w)
  const neighbors = stats.patternBuckets.get(pat) ?? 1
  const confusabilityEase = Math.min(1, Math.log2(neighbors) / 4) // 1 if many neighbors, ~0 if unique
  const confusabilityPenalty = 1 - confusabilityEase // convert to “hardness”

  // Weighted blend 
  const score0to1 =
      0.22 * letterRarityNorm +
      0.18 * posRarityNorm +
      0.15 * vowelPenalty +
      0.12 * repeatPenalty +
      0.15 * clusterPenalty +
      0.12 * bigramRarityNorm +
      0.06 * confusabilityPenalty

  return Math.round(score0to1 * 100)
}

export function buildPipTierer(stats: Stats) {
  const scores = WORDS.map(w => scoreDifficulty(w, stats))
  const sorted = [...scores].sort((a, b) => a - b)
  const q = (p: number) => sorted[Math.floor(p * (sorted.length - 1))]
  const q25 = q(0.25)
  const q50 = q(0.50)
  const q75 = q(0.75)

  return function tierFor(word: string): { pips: string; name: string; score: number } {
    const s = scoreDifficulty(word, stats)
    let pipsCount: number
    let name: string
    if (s <= q25)      { pipsCount = 1; name = 'Ensign' }
    else if (s <= q50) { pipsCount = 2; name = 'Lieutenant' }
    else if (s <= q75) { pipsCount = 3; name = 'Commander' }
    else               { pipsCount = 4; name = 'Captain' }
    return { pips: '🔘'.repeat(pipsCount), name, score: s }
  }
}

const STATS = buildStats(VALIDGUESSES) // use VALIDGUESSES to reflect what players can type
export const tierForWord = buildPipTierer(STATS)

export const isWordInWordList = (word: string) => {
  return (
    WORDS.includes(word.toLowerCase()) ||
    VALIDGUESSES.includes(word.toLowerCase())
  )
}

export const isWinningWord = (word: string) => {
  return solution === word
}

/** ---------- deterministic shuffle utilities ---------- */
/**
 * xmur3: string -> 32-bit seed
 * sfc32: fast deterministic PRNG
 * Together they give us a stable PRNG from a seed string.
 * (Good enough for shuffling; not cryptographically secure.)
 */
function xmur3(str: string) {
  let h = 1779033703 ^ str.length
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }
  return function () {
    h = Math.imul(h ^ (h >>> 16), 2246822507)
    h = Math.imul(h ^ (h >>> 13), 3266489909)
    return (h ^= h >>> 16) >>> 0
  }
}
function sfc32(a: number, b: number, c: number, d: number) {
  return function () {
    a >>>= 0; b >>>= 0; c >>>= 0; d >>>= 0
    let t = (a + b) | 0
    a = b ^ (b >>> 9)
    b = (c + (c << 3)) | 0
    c = ((c << 21) | (c >>> 11)) | 0
    d = (d + 1) | 0
    t = (t + d) | 0
    c = (c + t) | 0
    // 0..1
    return (t >>> 0) / 4294967296
  }
}

/**
 * Build a deterministic permutation of an array using Fisher–Yates
 * driven by the seeded PRNG.
 */
function deterministicShuffle<T>(arr: T[], seedStr: string): T[] {
  const seed = xmur3(seedStr)
  const rng = sfc32(seed(), seed(), seed(), seed())
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    const tmp = a[i]
    a[i] = a[j]
    a[j] = tmp
  }
  return a
}

const EPOCH = new Date(2022, 0, 1, 0, 0, 0, 0)

const BASE_SEED = 'you-goobers-quit-reading' 

let cachedSeasonIndex: number | null = null
let cachedShuffledWords: string[] | null = null

function getDayIndexSinceEpoch(): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = new Date(EPOCH)
  start.setHours(0, 0, 0, 0)

  // Faster than while-loop day stepping:
  const msInDay = 24 * 60 * 60 * 1000
  const index = Math.floor((today.getTime() - start.getTime()) / msInDay)
  return Math.max(0, index)
}

function getShuffledWordsForSeason(seasonIndex: number): string[] {
  if (cachedSeasonIndex === seasonIndex && cachedShuffledWords) {
    return cachedShuffledWords
  }
  const seedForThisSeason = `${BASE_SEED}::season-${seasonIndex}`
  const shuffled = deterministicShuffle(WORDS, seedForThisSeason)
  cachedSeasonIndex = seasonIndex
  cachedShuffledWords = shuffled
  return shuffled
}

export const getWordOfDay = () => {
  const index = getDayIndexSinceEpoch()

  // Compute today’s “season” (0-based)
  const seasonIndex = Math.floor(index / WORDS.length)

  // Position within the current season
  const within = index % WORDS.length

  // Use the season’s permutation
  const shuffled = getShuffledWordsForSeason(seasonIndex)

  const todayWord = shuffled[within]
  const todayUpper = todayWord.toUpperCase()

  // Tomorrow (local midnight + 1 day)
  const nextDay = new Date()
  nextDay.setHours(0, 0, 0, 0)
  nextDay.setDate(nextDay.getDate() + 1)

  return {
    solution: todayUpper,
    solutionIndex: index, 
    tomorrow: nextDay.valueOf(),
  }
}

export const { solution, solutionIndex, tomorrow } = getWordOfDay()

// Difficulty → pips + rank name
const todayTier = tierForWord(solution)
export const currentRank = todayTier.pips
export const currentRankName = todayTier.name
export const currentDifficultyScore = todayTier.score

// (Optional legacy helpers — keep only if other files still import these)
export const currentDay = () => new Date().getDay()
export const calcCurrentRank = () => currentRank
export const calcCurrentRankName = () => currentRankName

export const calcCurrentWeek = () => {
	// adapted from yunandtidus from Stackoverflow
	var currentDate = new Date()
	var currentYear = currentDate.getFullYear()
	var tempDate = new Date()
	tempDate.setHours(0,0,0,0)
	tempDate.setMonth(0)
	tempDate.setFullYear(currentYear)
	tempDate.setDate(1)
	var totalSundays = 0
	for (var i = tempDate; i <= currentDate; ) {
		if (i.getDay() === 0) {
			totalSundays++
		}
		i.setTime(i.getTime() + 1000*60*60*24)
	}

	if (totalSundays > 38) {
		return 38
	}
	else {
		return totalSundays;
	}
}
export const currentWeek = calcCurrentWeek()
